import { useEffect, useMemo, useState } from 'react'
import {
  Calculator,
  TreeStructure,
  Scales,
  ListBullets,
  MagnifyingGlass,
  CaretDown,
  CaretRight,
  CheckCircle,
  Warning,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { exportarExcel, exportarPDF } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { hoyGT } from '../lib/fechas'

/**
 * Módulo de Contabilidad de NeoCore — vista de Hugo durante el sprint
 * de migración del COBASY.
 *
 *   1. Balance General  — Activo = Pasivo + Capital con drill-down
 *   2. Catálogo INGECOP — árbol jerárquico con búsqueda
 *   3. Asientos         — lista cronológica expandible
 *
 * Estilos: usa el design language Neocoop (CSS vars + .card, .table,
 * .kpi-grid, .badge, .tab-btn) — no Tailwind.
 */

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type CuentaContable = {
  codigo: string
  nombre: string
  clase: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'
  padre_codigo: string | null
  nivel: number
  naturaleza: 'DEUDORA' | 'ACREEDORA'
  acepta_partidas: boolean
  moneda: 'GTQ' | 'USD'
  activa: boolean
}

type Asiento = {
  id: string
  numero: number
  fecha: string
  descripcion: string
  origen: 'APERTURA' | 'AUTOMATICO' | 'MANUAL' | 'AJUSTE' | 'CIERRE'
  estado: 'vigente' | 'anulado'
  total_debe: number
  total_haber: number
  asociado_id: string | null
  credito_id: string | null
}

type Partida = {
  id: string
  asiento_id: string
  cuenta_codigo: string
  tipo: 'DEBE' | 'HABER'
  monto: number
  descripcion: string | null
  orden: number
  cuenta_nombre?: string
}

// Color por clase usado en chips/badges
const CLASE_COLORS: Record<string, { bg: string; fg: string }> = {
  '1': { bg: 'rgba(22, 163, 74, 0.10)',  fg: '#166534' },  // Activo — verde
  '2': { bg: 'rgba(217, 119, 6, 0.12)',  fg: '#9a5a03' },  // Regularizadoras — ámbar
  '3': { bg: 'rgba(220, 38, 38, 0.10)',  fg: '#b91c1c' },  // Pasivo — rojo
  '4': { bg: 'rgba(220, 38, 38, 0.10)',  fg: '#b91c1c' },
  '5': { bg: 'rgba(255, 91, 4, 0.10)',   fg: 'var(--brand-700)' },  // Capital — brand
  '6': { bg: 'rgba(124, 58, 237, 0.10)', fg: '#6d28d9' },  // Productos — violeta
  '7': { bg: 'rgba(217, 119, 6, 0.12)',  fg: '#9a5a03' },  // Gastos — ámbar
  '8': { bg: 'var(--n-200)',             fg: 'var(--n-700)' },
  '9': { bg: 'var(--n-200)',             fg: 'var(--n-700)' },
}

const fmtQ = (n: number) =>
  `Q ${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${n < 0 ? ' (-)' : ''}`

const fmtFecha = (s: string) => {
  const d = new Date(s + 'T00:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

type Tab = 'balance' | 'catalogo' | 'asientos'

export function NeoCoreContabilidadPage() {
  const [tab, setTab] = useState<Tab>('balance')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'var(--brand-50)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--brand-600)',
          }}
        >
          <Calculator size={22} weight="duotone" />
        </div>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: 0, letterSpacing: '-0.4px' }}>
            Contabilidad
          </h1>
          <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '2px 0 0' }}>
            Catálogo INGECOP · Balance de saldos y mayor auxiliar
          </p>
        </div>
      </header>

      <nav style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        <TabBtn active={tab === 'balance'} onClick={() => setTab('balance')} icon={Scales}>
          Balance de saldos
        </TabBtn>
        <TabBtn active={tab === 'catalogo'} onClick={() => setTab('catalogo')} icon={TreeStructure}>
          Catálogo de Cuentas
        </TabBtn>
        <TabBtn active={tab === 'asientos'} onClick={() => setTab('asientos')} icon={ListBullets}>
          Asientos
        </TabBtn>
      </nav>

      {tab === 'balance' && <BalanceTab />}
      {tab === 'catalogo' && <CatalogoTab />}
      {tab === 'asientos' && <AsientosSection />}
    </div>
  )
}

function TabBtn({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: typeof Calculator
  children: React.ReactNode
}) {
  return (
    <button
      className={['tab-btn', active ? 'active' : ''].join(' ')}
      onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      <Icon size={15} weight={active ? 'fill' : 'regular'} />
      {children}
    </button>
  )
}

// -----------------------------------------------------------------------------
// Tab: Balance General
// -----------------------------------------------------------------------------

type ComprobRow = {
  codigo: string
  nombre: string
  nivel: number
  naturaleza: 'DEUDORA' | 'ACREEDORA'
  saldo_inicial: number
  debe_mes: number
  haber_mes: number
  saldo_final_deudor: number
  saldo_final_acreedor: number
  anomalo: boolean
}

// Nivel de agregación por longitud de código INGECOP (jerárquico)
const NIVELES: { v: string; label: string }[] = [
  { v: '',  label: 'Analítico (detalle)' },
  { v: '4', label: 'Cuenta Mayor' },
  { v: '3', label: 'Rubro' },
  { v: '1', label: 'Clase' },
]

const money = (n: number) =>
  Math.abs(n) > 0.005
    ? `Q ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—'

/**
 * Balance de Comprobación de Sumas y Saldos (INGECOP).
 * Sumas del período por cuenta + split saldo deudor/acreedor (por signo, para
 * garantizar el cuadre) con marca de saldo contrario/anómalo por naturaleza.
 * Doble verificación mandatoria: Σdebe=Σhaber y Σsaldo_deudor=Σsaldo_acreedor.
 */
function BalanceTab() {
  const hoyStr = hoyGT()
  // Draft: lo que el usuario edita en los controles. Solo se aplica al hacer clic
  // en "Aplicar filtros" (no en onChange) — así el date picker no se interrumpe.
  const [desde, setDesde] = useState(hoyStr.slice(0, 4) + '-01-01')
  const [hasta, setHasta] = useState(hoyStr)
  const [nivel, setNivel] = useState('')            // '' = analítico
  const [ocultarCeros, setOcultarCeros] = useState(true)
  // Query aplicada: lo que realmente dispara el RPC.
  const [query, setQuery] = useState({ desde: hoyStr.slice(0, 4) + '-01-01', hasta: hoyStr, nivel: '', ocultarCeros: true })
  const [data, setData] = useState<ComprobRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const aplicar = () => setQuery({ desde, hasta, nivel, ocultarCeros })
  const filtrosSinAplicar =
    desde !== query.desde || hasta !== query.hasta || nivel !== query.nivel || ocultarCeros !== query.ocultarCeros

  useEffect(() => {
    if (!query.desde || !query.hasta) return
    setLoading(true)
    supabase
      .rpc('balance_comprobacion_periodo', {
        p_desde: query.desde,
        p_hasta: query.hasta,
        p_nivel: query.nivel ? Number(query.nivel) : null,
        p_ocultar_ceros: query.ocultarCeros,
      })
      .then(({ data, error }) => {
        if (error) { setError(error.message); setLoading(false); return }
        setError(null)
        setData((data ?? []) as ComprobRow[])
        setLoading(false)
      })
  }, [query])

  const tot = useMemo(
    () => data.reduce(
      (a, r) => ({
        inicial: a.inicial + Number(r.saldo_inicial),
        debe: a.debe + Number(r.debe_mes),
        haber: a.haber + Number(r.haber_mes),
        deudor: a.deudor + Number(r.saldo_final_deudor),
        acreedor: a.acreedor + Number(r.saldo_final_acreedor),
      }),
      { inicial: 0, debe: 0, haber: 0, deudor: 0, acreedor: 0 },
    ),
    [data],
  )

  const difSumas = Math.abs(tot.debe - tot.haber)
  const difSaldos = Math.abs(tot.deudor - tot.acreedor)
  const cuadraSumas = difSumas < 0.005
  const cuadraSaldos = difSaldos < 0.005
  const cuadrado = cuadraSumas && cuadraSaldos
  const anomalas = useMemo(() => data.filter((r) => r.anomalo).length, [data])
  const nivelLabel = NIVELES.find((n) => n.v === query.nivel)?.label ?? 'Analítico'

  async function exportar(formato: 'pdf' | 'excel') {
    const config = await fetchConfigReporte()
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    // Formato de moneda a 2 decimales (evita el floating point crudo)
    const q = (n: number) => `Q ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

    // Verificación por clase (saldo final, en su naturaleza):
    //   1 Activo · 2 Regularizadoras · 3 Pasivo · 4 Otras acreedoras
    //   5 Capital · 6 Productos · 7 Gastos
    const nat = (r: ComprobRow) =>
      r.naturaleza === 'DEUDORA'
        ? Number(r.saldo_final_deudor) - Number(r.saldo_final_acreedor)
        : Number(r.saldo_final_acreedor) - Number(r.saldo_final_deudor)
    const porClase: Record<string, number> = {}
    for (const r of data) { const c = r.codigo[0]; porClase[c] = (porClase[c] ?? 0) + nat(r) }
    const c = (k: string) => porClase[k] ?? 0
    const totalActivo = c('1') - c('2')
    const estadoResultados = c('6') - c('7')
    // El resultado del ejercicio (utilidad/pérdida) se integra al patrimonio,
    // así el Activo cuadra con Pasivo + Capital.
    const totalPasivoCapital = c('3') + c('4') + c('5') + estadoResultados
    const diferencia = totalActivo - totalPasivoCapital

    const resumen = [
      { label: '(+) ACTIVO', valor: q(c('1')) },
      { label: '(-) REGULARIZADORAS DE ACTIVO', valor: q(c('2')) },
      { label: 'TOTAL ACTIVO', valor: q(totalActivo), bold: true },
      { label: '', valor: '' },
      { label: '(+) PASIVO', valor: q(c('3')) },
      { label: '(+) CUENTAS ACREEDORAS', valor: q(c('4')) },
      { label: '(+) CAPITAL CONTABLE', valor: q(c('5')) },
      { label: `(${estadoResultados >= 0 ? '+' : '-'}) RESULTADO DEL EJERCICIO`, valor: q(estadoResultados) },
      { label: 'TOTAL PASIVO Y CAPITAL', valor: q(totalPasivoCapital), bold: true },
      { label: '', valor: '' },
      { label: '(+) PRODUCTOS', valor: q(c('6')) },
      { label: '(-) GASTOS', valor: q(c('7')) },
      { label: 'ESTADO DE RESULTADOS', valor: q(estadoResultados), bold: true },
      { label: '', valor: '' },
      { label: 'DIFERENCIA', valor: q(diferencia), bold: true },
    ]

    await fn({
      config,
      titulo: 'Balance de Comprobación de Saldos',
      subtitulo: `Del ${query.desde} al ${query.hasta} · ${nivelLabel}`,
      headers: ['Código', 'Cuenta', 'Nivel', 'Saldo inicial', 'Debe', 'Haber', 'Saldo final deudor', 'Saldo final acreedor'],
      rows: data.map((r) => [
        r.codigo, r.nombre + (r.anomalo ? '  ⚠ saldo contrario' : ''), r.nivel,
        q(Number(r.saldo_inicial)), q(Number(r.debe_mes)), q(Number(r.haber_mes)),
        q(Number(r.saldo_final_deudor)), q(Number(r.saldo_final_acreedor)),
      ]),
      totalRow: ['', 'TOTALES', '', q(tot.inicial), q(tot.debe), q(tot.haber), q(tot.deudor), q(tot.acreedor)],
      orientacion: 'landscape',
      columnasNumericas: [3, 4, 5, 6, 7],
      resumen,
      filename: `balance-comprobacion-saldos-${query.desde}-al-${query.hasta}`,
    })
  }

  if (loading) return <Loading />
  if (error) return <ErrorBox error={error} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Filtros */}
      <div style={{ display: 'flex', alignItems: 'end', gap: 10, flexWrap: 'wrap' }}>
        <label style={filtroLbl}>DESDE
          <input type="date" className="input" value={desde} max={hasta}
            onChange={(e) => setDesde(e.target.value)} style={{ fontSize: 13 }} />
        </label>
        <label style={filtroLbl}>HASTA
          <input type="date" className="input" value={hasta} min={desde}
            onChange={(e) => setHasta(e.target.value)} style={{ fontSize: 13 }} />
        </label>
        <label style={filtroLbl}>NIVEL DE AGREGACIÓN
          <select className="input" value={nivel} onChange={(e) => setNivel(e.target.value)} style={{ fontSize: 13 }}>
            {NIVELES.map((n) => <option key={n.v} value={n.v}>{n.label}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink)', height: 34 }}>
          <input type="checkbox" checked={ocultarCeros} onChange={(e) => setOcultarCeros(e.target.checked)} />
          Ocultar cuentas sin movimiento
        </label>
        <button className={filtrosSinAplicar ? 'btn-primary' : 'btn-secondary'} onClick={aplicar}
          disabled={!filtrosSinAplicar}
          style={{ fontSize: 13, height: 34 }}>
          Aplicar filtros
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn-secondary" onClick={() => exportar('pdf')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>⬇ PDF</button>
          <button className="btn-secondary" onClick={() => exportar('excel')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>⬇ Excel</button>
        </div>
      </div>

      {/* Doble verificación de cuadre (mandatoria) */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          {cuadrado
            ? <CheckCircle size={22} weight="fill" color="var(--success)" />
            : <Warning size={22} weight="fill" color="var(--error)" />}
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
            {cuadrado ? 'Balance cuadrado · doble verificación OK' : 'BALANCE DESCUADRADO'}
          </h2>
          <span className={cuadrado ? 'badge badge-success' : 'badge badge-danger'} style={{ marginLeft: 'auto' }}>
            {cuadrado ? '✓ OK' : '⚠ revisar'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <CuadreCell titulo="Σ Sumas · Debe = Haber" ok={cuadraSumas}
            izq={tot.debe} der={tot.haber} dif={difSumas} />
          <CuadreCell titulo="Σ Saldos · Deudor = Acreedor" ok={cuadraSaldos}
            izq={tot.deudor} der={tot.acreedor} dif={difSaldos} />
        </div>
        {!cuadrado && (
          <div style={{
            marginTop: 14, padding: '10px 14px', background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.18)', borderRadius: 'var(--radius-md)',
            fontSize: 13, color: 'var(--error)', fontWeight: 600,
          }}>
            No se puede cerrar el período: el balance está descuadrado. Revisá las partidas antes de continuar.
          </div>
        )}
        {cuadrado && anomalas > 0 && (
          <div style={{
            marginTop: 14, padding: '10px 14px', background: 'rgba(217,119,6,0.08)',
            border: '1px solid rgba(217,119,6,0.22)', borderRadius: 'var(--radius-md)',
            fontSize: 13, color: '#9a5a03',
          }}>
            {anomalas} {anomalas === 1 ? 'cuenta' : 'cuentas'} con <strong>saldo contrario a su naturaleza</strong> (resaltadas abajo). El balance cuadra, pero conviene revisarlas.
          </div>
        )}
      </div>

      {/* Matriz de comprobación */}
      <div>
        <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.6px', textTransform: 'uppercase', margin: '0 0 12px' }}>
          Balance de comprobación · {nivelLabel} ({data.length})
        </h3>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Código</th>
                <th>Nombre de la cuenta</th>
                <th style={{ width: 50, textAlign: 'center' }}>Nivel</th>
                <th style={{ textAlign: 'right', width: 120 }}>Saldo inicial</th>
                <th style={{ textAlign: 'right', width: 110 }}>Debe</th>
                <th style={{ textAlign: 'right', width: 110 }}>Haber</th>
                <th style={{ textAlign: 'right', width: 120 }}>Saldo final deudor</th>
                <th style={{ textAlign: 'right', width: 120 }}>Saldo final acreedor</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin cuentas en el período</td></tr>
              ) : data.map((r) => (
                <tr key={r.codigo} style={r.anomalo ? { background: 'rgba(220,38,38,0.05)' } : undefined}>
                  <td><span className="mono" style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{r.codigo}</span></td>
                  <td>
                    <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{r.nombre}</span>
                    {r.anomalo && (
                      <span title="Saldo contrario a la naturaleza de la cuenta" style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(220,38,38,0.12)', color: 'var(--error)',
                      }}>⚠ contrario</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted-foreground)' }}>{r.nivel}</td>
                  <td style={cellNum(r.saldo_inicial)}>{money(Number(r.saldo_inicial))}</td>
                  <td style={cellNum(r.debe_mes)}>{money(Number(r.debe_mes))}</td>
                  <td style={cellNum(r.haber_mes)}>{money(Number(r.haber_mes))}</td>
                  <td style={cellNum(r.saldo_final_deudor, r.anomalo)}>{money(Number(r.saldo_final_deudor))}</td>
                  <td style={cellNum(r.saldo_final_acreedor, r.anomalo)}>{money(Number(r.saldo_final_acreedor))}</td>
                </tr>
              ))}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr style={{ background: 'var(--n-50)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                  <td colSpan={3} style={{ textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 12, color: 'var(--muted-foreground)' }}>
                    Totales consolidados
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(tot.inicial)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: cuadraSumas ? 'var(--ink)' : 'var(--error)' }}>{money(tot.debe)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: cuadraSumas ? 'var(--ink)' : 'var(--error)' }}>{money(tot.haber)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: cuadraSaldos ? 'var(--ink)' : 'var(--error)' }}>{money(tot.deudor)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: cuadraSaldos ? 'var(--ink)' : 'var(--error)' }}>{money(tot.acreedor)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}

const filtroLbl: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11,
  color: 'var(--muted-foreground)', fontWeight: 600,
}

function cellNum(v: number, anomalo?: boolean): React.CSSProperties {
  return {
    textAlign: 'right', fontVariantNumeric: 'tabular-nums',
    color: anomalo ? 'var(--error)' : Math.abs(v) > 0.005 ? 'var(--ink)' : 'var(--n-300)',
    fontWeight: Math.abs(v) > 0.005 ? 500 : 400,
  }
}

/** Celda de verificación de cuadre (izq = der). */
function CuadreCell({ titulo, ok, izq, der, dif }: {
  titulo: string; ok: boolean; izq: number; der: number; dif: number
}) {
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 'var(--radius-md)',
      border: `1px solid ${ok ? 'var(--border)' : 'rgba(220,38,38,0.3)'}`,
      background: ok ? 'var(--n-50)' : 'rgba(220,38,38,0.04)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        {ok ? <CheckCircle size={14} weight="fill" color="var(--success)" /> : <Warning size={14} weight="fill" color="var(--error)" />}
        {titulo}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
          Q {izq.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        <span style={{ color: ok ? 'var(--success)' : 'var(--error)', fontWeight: 700 }}>{ok ? '=' : '≠'}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
          Q {der.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
      {!ok && (
        <div style={{ fontSize: 12, color: 'var(--error)', marginTop: 4 }}>
          Diferencia: Q {dif.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Tab: Catálogo
// -----------------------------------------------------------------------------

function CatalogoTab() {
  const [data, setData] = useState<CuentaContable[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['1', '2', '3', '5', '6', '7']))
  const [nuevaOpen, setNuevaOpen] = useState(false)
  const [refetch, setRefetch] = useState(0)

  useEffect(() => {
    supabase
      .from('cuenta_contable')
      .select('*')
      // Catálogo completo: también inactivas/sin saldo (pedido Carlitos)
      .order('codigo')
      .then(({ data }) => {
        setData((data ?? []) as CuentaContable[])
        setLoading(false)
      })
  }, [refetch])

  const matched = useMemo(() => {
    if (!search) return data
    const q = search.toLowerCase()
    return data.filter((c) => c.codigo.includes(q) || c.nombre.toLowerCase().includes(q))
  }, [data, search])

  const toggle = (codigo: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(codigo)) next.delete(codigo)
      else next.add(codigo)
      return next
    })
  }

  if (loading) return <Loading />

  const childrenOf = (padre: string | null): CuentaContable[] =>
    matched.filter((c) => c.padre_codigo === padre).sort((a, b) => a.codigo.localeCompare(b.codigo))

  const root = childrenOf(null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div className="search-input-wrap" style={{ maxWidth: 420 }}>
          <MagnifyingGlass className="search-icon" size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre…"
          />
        </div>
        <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
          {data.length} cuentas · {data.filter((c) => c.acepta_partidas).length} operativas
        </span>
        <button
          className="btn-primary"
          onClick={() => setNuevaOpen(true)}
          style={{ marginLeft: 'auto', fontSize: 13 }}
        >
          + Nueva cuenta
        </button>
      </div>

      {nuevaOpen && (
        <NuevaCuentaModal
          cuentas={data}
          onClose={() => setNuevaOpen(false)}
          onCreated={() => { setNuevaOpen(false); setRefetch((n) => n + 1) }}
        />
      )}

      <div className="card" style={{ padding: 8 }}>
        {root.map((c) => (
          <CuentaNode
            key={c.codigo}
            cuenta={c}
            childrenOf={childrenOf}
            expanded={expanded}
            toggle={toggle}
            forceOpen={!!search}
          />
        ))}
      </div>
    </div>
  )
}

function CuentaNode({
  cuenta,
  childrenOf,
  expanded,
  toggle,
  forceOpen,
}: {
  cuenta: CuentaContable
  childrenOf: (padre: string | null) => CuentaContable[]
  expanded: Set<string>
  toggle: (codigo: string) => void
  forceOpen: boolean
}) {
  const hijos = childrenOf(cuenta.codigo)
  const isOpen = forceOpen || expanded.has(cuenta.codigo)
  const tieneHijos = hijos.length > 0
  const color = CLASE_COLORS[cuenta.clase]
  const indent = Math.max(0, cuenta.nivel - 1) * 16

  return (
    <div>
      <div
        onClick={() => tieneHijos && toggle(cuenta.codigo)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          paddingLeft: 10 + indent,
          borderRadius: 8,
          cursor: tieneHijos ? 'pointer' : 'default',
          transition: 'background 80ms',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--n-50)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {tieneHijos ? (
          isOpen ? <CaretDown size={12} color="var(--muted-foreground)" /> : <CaretRight size={12} color="var(--muted-foreground)" />
        ) : (
          <span style={{ width: 12 }} />
        )}
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            minWidth: 110,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {cuenta.codigo}
        </span>
        <span
          style={{
            fontSize: 13,
            color: 'var(--ink)',
            fontWeight: cuenta.acepta_partidas ? 500 : 600,
            flex: 1,
          }}
        >
          {cuenta.nombre}
        </span>
        {cuenta.acepta_partidas && (
          <span
            style={{
              fontSize: 10,
              padding: '2px 7px',
              borderRadius: 4,
              background: 'var(--brand-50)',
              color: 'var(--brand-700)',
              fontWeight: 600,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
            }}
          >
            Operativa
          </span>
        )}
        <span
          style={{
            fontSize: 10,
            color: 'var(--muted-foreground)',
            minWidth: 70,
            textAlign: 'right',
          }}
        >
          {cuenta.naturaleza === 'DEUDORA' ? 'Deudora' : 'Acreedora'}
        </span>
        <span
          style={{
            fontSize: 10,
            padding: '2px 7px',
            borderRadius: 4,
            background: color.bg,
            color: color.fg,
            fontWeight: 700,
            minWidth: 18,
            textAlign: 'center',
          }}
        >
          {cuenta.clase}
        </span>
      </div>
      {isOpen && tieneHijos && (
        <div>
          {hijos.map((h) => (
            <CuentaNode
              key={h.codigo}
              cuenta={h}
              childrenOf={childrenOf}
              expanded={expanded}
              toggle={toggle}
              forceOpen={forceOpen}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Tab: Asientos
// -----------------------------------------------------------------------------

// Contenedor con sub-pestañas: Libro Diario (por movimiento) y Póliza Diaria (consolidada)
function AsientosSection() {
  const [sub, setSub] = useState<'diario' | 'diaria'>('diario')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        <button className={['tab-btn', sub === 'diario' ? 'active' : ''].join(' ')} onClick={() => setSub('diario')} style={{ fontSize: 13 }}>
          Libro Diario · por movimiento
        </button>
        <button className={['tab-btn', sub === 'diaria' ? 'active' : ''].join(' ')} onClick={() => setSub('diaria')} style={{ fontSize: 13 }}>
          Póliza Diaria · resumen por día
        </button>
      </div>
      {sub === 'diario' ? <AsientosTab /> : <PolizaDiariaTab />}
    </div>
  )
}

type DiaRow = {
  fecha: string
  correlativo_dia: number
  total_debe: number
  total_haber: number
  partidas: { cuenta_codigo: string; cuenta_nombre: string | null; debe: number; haber: number }[]
}

function PolizaDiariaTab() {
  const hoy = hoyGT()
  const [desde, setDesde] = useState(hoy.slice(0, 4) + '-01-01')
  const [hasta, setHasta] = useState(hoy)
  const [query, setQuery] = useState({ desde: hoy.slice(0, 4) + '-01-01', hasta: hoy })
  const aplicar = () => setQuery({ desde, hasta })
  const filtrosSinAplicar = desde !== query.desde || hasta !== query.hasta
  const [dias, setDias] = useState<DiaRow[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!query.desde || !query.hasta) return
    setLoading(true)
    supabase.rpc('poliza_diaria_periodo', { p_desde: query.desde, p_hasta: query.hasta })
      .then(({ data }) => {
        const rows = (data ?? []) as DiaRow[]
        setDias(rows)
        if (rows.length > 0) setExpanded(new Set([rows[0].fecha]))
        setLoading(false)
      })
  }, [query])

  async function exportarDiaria() {
    const config = await fetchConfigReporte()
    const rows: (string | number)[][] = []
    let tD = 0, tH = 0
    for (const d of dias) {
      for (const p of d.partidas) {
        rows.push([d.correlativo_dia, d.fecha, p.cuenta_codigo, p.cuenta_nombre ?? '',
          Number(p.debe) || '', Number(p.haber) || ''])
        tD += Number(p.debe); tH += Number(p.haber)
      }
    }
    await exportarExcel({
      config,
      titulo: 'Pólizas Diarias (resumen por día)',
      subtitulo: `Del ${query.desde} al ${query.hasta}`,
      headers: ['Correlativo día', 'Fecha', 'Cuenta', 'Nombre cuenta', 'Debe', 'Haber'],
      rows,
      totalRow: ['', '', '', 'TOTALES', tD, tH],
      filename: `polizas-diarias-${query.desde}-al-${query.hasta}`,
    })
  }

  const toggle = (f: string) => setExpanded((prev) => {
    const n = new Set(prev); n.has(f) ? n.delete(f) : n.add(f); return n
  })

  const filtros = (
    <div style={{ display: 'flex', alignItems: 'end', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600 }}>
        DESDE
        <input type="date" className="input" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} style={{ fontSize: 13 }} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600 }}>
        HASTA
        <input type="date" className="input" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} style={{ fontSize: 13 }} />
      </label>
      <button className={filtrosSinAplicar ? 'btn-primary' : 'btn-secondary'} onClick={aplicar}
        disabled={!filtrosSinAplicar} style={{ fontSize: 13, height: 34 }}>Aplicar filtros</button>
      <button className="btn-secondary" onClick={exportarDiaria} disabled={dias.length === 0} style={{ fontSize: 13, height: 34 }}>⬇ Exportar Excel</button>
    </div>
  )

  if (loading) return <Loading />

  return (
    <div>
      {filtros}
      {dias.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--muted-foreground)', margin: 0 }}>No hay movimientos entre {query.desde} y {query.hasta}.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {dias.map((d) => {
            const isOpen = expanded.has(d.fecha)
            const cuadra = Math.abs(Number(d.total_debe) - Number(d.total_haber)) < 0.005
            return (
              <div key={d.fecha} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <button onClick={() => toggle(d.fecha)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  {isOpen ? <CaretDown size={14} color="var(--muted-foreground)" /> : <CaretRight size={14} color="var(--muted-foreground)" />}
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, background: 'var(--brand-50)', color: 'var(--brand-700)', fontWeight: 700, fontSize: 13 }}>
                    #{d.correlativo_dia}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>Póliza diaria · {fmtFecha(d.fecha)}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, display: 'flex', gap: 10 }}>
                      <span>{d.partidas.length} cuentas</span>
                      <span>·</span>
                      <span className={cuadra ? 'badge badge-success' : 'badge badge-danger'} style={{ fontSize: 10 }}>{cuadra ? '✓ cuadrada' : '⚠ revisar'}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{fmtQ(d.total_debe)}</div>
                  </div>
                </button>
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', background: 'var(--n-50)' }}>
                    <table className="table" style={{ background: 'transparent' }}>
                      <thead><tr>
                        <th style={{ width: 140 }}>Cuenta</th><th>Nombre</th>
                        <th style={{ textAlign: 'right', width: 150 }}>Debe</th>
                        <th style={{ textAlign: 'right', width: 150 }}>Haber</th>
                      </tr></thead>
                      <tbody>
                        {d.partidas.map((p, pi) => (
                          <tr key={`${p.cuenta_codigo}-${p.debe > 0 ? 'd' : 'h'}-${pi}`}>
                            <td><span className="mono" style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{p.cuenta_codigo}</span></td>
                            <td style={{ fontSize: 12, color: 'var(--ink)' }}>{p.cuenta_nombre ?? '—'}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: Number(p.debe) > 0 ? 'var(--ink)' : 'var(--n-300)' }}>{Number(p.debe) > 0 ? fmtQ(p.debe) : '—'}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: Number(p.haber) > 0 ? 'var(--ink)' : 'var(--n-300)' }}>{Number(p.haber) > 0 ? fmtQ(p.haber) : '—'}</td>
                          </tr>
                        ))}
                        <tr style={{ background: 'var(--surface)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                          <td colSpan={2} style={{ textAlign: 'right', fontSize: 12, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Totales</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtQ(d.total_debe)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtQ(d.total_haber)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function AsientosTab() {
  const [asientos, setAsientos] = useState<Asiento[]>([])
  const [partidas, setPartidas] = useState<Map<string, Partida[]>>(new Map())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [refetch, setRefetch] = useState(0)
  const [anularTarget, setAnularTarget] = useState<Asiento | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // Filtros de fecha (draft) — se aplican con botón, no en onChange.
  const hoy = hoyGT()
  const [desde, setDesde] = useState(hoy.slice(0, 4) + '-01-01')
  const [hasta, setHasta] = useState(hoy)
  const [query, setQuery] = useState({ desde: hoy.slice(0, 4) + '-01-01', hasta: hoy })
  const aplicar = () => setQuery({ desde, hasta })
  const filtrosSinAplicar = desde !== query.desde || hasta !== query.hasta

  // Botón nueva póliza manual (elemento, NO componente: evita remounts que
  // rompen el foco del date picker en cada render)
  const nuevaPolizaBtn = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <a
        href="/neocore/contabilidad/poliza-nueva"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', background: 'var(--brand-600, #FF5B04)', color: '#fff',
          borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none',
        }}
      >
        + Nueva póliza manual
      </a>
      <button
        onClick={() => setImportOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', background: 'var(--surface)', color: 'var(--ink)',
          border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
        }}
      >
        ⬆ Importar Excel
      </button>
    </div>
  )

  useEffect(() => {
    if (!query.desde || !query.hasta) return
    setLoading(true)
    // RPC libro_diario_periodo: cada asiento trae sus partidas anidadas en JSON,
    // así el detalle nunca se trunca por el límite de filas de PostgREST.
    supabase
      .rpc('libro_diario_periodo', { p_desde: query.desde, p_hasta: query.hasta })
      .then(({ data }) => {
        const rows = (data ?? []) as (Asiento & { partidas: Omit<Partida, 'asiento_id'>[] })[]
        const m = new Map<string, Partida[]>()
        const asientosArr: Asiento[] = []
        for (const r of rows) {
          const { partidas: ps, ...a } = r
          asientosArr.push(a as Asiento)
          m.set(a.id, ((ps ?? []) as Omit<Partida, 'asiento_id'>[]).map((p) => ({ ...p, asiento_id: a.id })))
        }
        setAsientos(asientosArr)
        setPartidas(m)
        // Auto-expandir el más reciente
        if (asientosArr.length > 0) setExpanded(new Set([asientosArr[0].id]))
        setLoading(false)
      })
  }, [query, refetch])

  // Export Excel de asientos con partidas (pedido Carlitos 10-ago)
  async function exportarAsientos() {
    const config = await fetchConfigReporte()
    const rows: (string | number)[][] = []
    let tDebe = 0, tHaber = 0
    for (const a of asientos) {
      for (const p of partidas.get(a.id) ?? []) {
        rows.push([
          a.numero, a.fecha, a.origen, a.descripcion,
          p.cuenta_codigo, p.cuenta_nombre ?? '',
          p.tipo === 'DEBE' ? p.monto : '',
          p.tipo === 'HABER' ? p.monto : '',
        ])
        if (p.tipo === 'DEBE') tDebe += p.monto
        else tHaber += p.monto
      }
    }
    await exportarExcel({
      config,
      titulo: 'Asientos Contables',
      subtitulo: `Del ${query.desde} al ${query.hasta}`,
      headers: ['No.', 'Fecha', 'Origen', 'Descripción', 'Cuenta', 'Nombre cuenta', 'Debe', 'Haber'],
      rows,
      totalRow: ['', '', '', 'TOTALES', '', '', tDebe, tHaber],
      filename: `asientos-${query.desde}-al-${query.hasta}`,
    })
  }

  const filtrosBar = (
    <div style={{ display: 'flex', alignItems: 'end', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600 }}>
        DESDE
        <input type="date" className="input" value={desde} max={hasta}
          onChange={(e) => setDesde(e.target.value)} style={{ fontSize: 13 }} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600 }}>
        HASTA
        <input type="date" className="input" value={hasta} min={desde}
          onChange={(e) => setHasta(e.target.value)} style={{ fontSize: 13 }} />
      </label>
      <button className={filtrosSinAplicar ? 'btn-primary' : 'btn-secondary'} onClick={aplicar}
        disabled={!filtrosSinAplicar} style={{ fontSize: 13, height: 34 }}>
        Aplicar filtros
      </button>
      <button className="btn-secondary" onClick={exportarAsientos} disabled={asientos.length === 0}
        style={{ fontSize: 13, height: 34 }}>
        ⬇ Exportar Excel
      </button>
    </div>
  )

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (loading) return <Loading />
  if (asientos.length === 0) {
    return (
      <>
        {nuevaPolizaBtn}
        {filtrosBar}
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--muted-foreground)', margin: 0 }}>
            No hay asientos vigentes entre {query.desde} y {query.hasta}.
          </p>
        </div>
      </>
    )
  }

  return (
    <div>
    {nuevaPolizaBtn}
    {filtrosBar}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {asientos.map((a) => {
        const isOpen = expanded.has(a.id)
        const ps = partidas.get(a.id) ?? []
        return (
          <div key={a.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <button
              onClick={() => toggle(a.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              {isOpen ? <CaretDown size={14} color="var(--muted-foreground)" /> : <CaretRight size={14} color="var(--muted-foreground)" />}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: a.origen === 'APERTURA' ? 'var(--brand-50)' : 'var(--n-100)',
                  color: a.origen === 'APERTURA' ? 'var(--brand-700)' : 'var(--n-700)',
                  fontWeight: 700,
                  fontSize: 14,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                #{a.numero}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--ink)',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.descripcion}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, display: 'flex', gap: 10 }}>
                  <span>{fmtFecha(a.fecha)}</span>
                  <span>·</span>
                  <span className={`badge ${a.origen === 'APERTURA' ? 'badge-en_proceso' : 'badge-muted'}`} style={{ fontSize: 10 }}>
                    {a.origen}
                  </span>
                  <span>·</span>
                  <span>{ps.length} partidas</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Total
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtQ(a.total_debe)}
                </div>
              </div>
            </button>
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border)', background: 'var(--n-50)' }}>
                <table className="table" style={{ background: 'transparent' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Cuenta</th>
                      <th>Descripción</th>
                      <th style={{ width: 80 }}>Tipo</th>
                      <th style={{ textAlign: 'right', width: 140 }}>Debe</th>
                      <th style={{ textAlign: 'right', width: 140 }}>Haber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ps.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div className="mono" style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{p.cuenta_codigo}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink)', marginTop: 2 }}>{p.cuenta_nombre ?? '—'}</div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{p.descripcion ?? '—'}</td>
                        <td>
                          <span
                            className={p.tipo === 'DEBE' ? 'badge badge-info' : 'badge badge-warn'}
                            style={{ fontSize: 11 }}
                          >
                            {p.tipo}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.tipo === 'DEBE' ? 'var(--ink)' : 'var(--n-300)' }}>
                          {p.tipo === 'DEBE' ? fmtQ(p.monto) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.tipo === 'HABER' ? 'var(--ink)' : 'var(--n-300)' }}>
                          {p.tipo === 'HABER' ? fmtQ(p.monto) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--surface)', borderTop: '2px solid var(--border)' }}>
                      <td colSpan={3} style={{ textAlign: 'right', fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Totales
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink)' }}>
                        {fmtQ(a.total_debe)}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink)' }}>
                        {fmtQ(a.total_haber)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
                  <a href={`/neocore/contabilidad/poliza/${a.id}/editar`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                      border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)',
                      fontSize: 12, textDecoration: 'none', color: 'var(--ink)' }}>
                    <PencilSimple size={13} /> Editar
                  </a>
                  <button onClick={() => setAnularTarget(a)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                      border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6, background: 'transparent',
                      fontSize: 12, cursor: 'pointer', color: 'var(--error)' }}>
                    <Trash size={13} /> Anular
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
    {anularTarget && (
      <AnularPolizaModal
        asiento={anularTarget}
        onClose={() => setAnularTarget(null)}
        onDone={() => { setAnularTarget(null); setRefetch((n) => n + 1) }}
      />
    )}
    {importOpen && (
      <ImportarPolizasModal
        onClose={() => setImportOpen(false)}
        onDone={(n) => { setImportOpen(false); setRefetch((k) => k + 1); alert(`${n} pólizas importadas`) }}
      />
    )}
    </div>
  )
}

// Modal de importación masiva de pólizas desde Excel
type PolizaImport = {
  correlativo: string
  fecha: string | null
  descripcion: string
  partidas: { cuenta_codigo: string; tipo: 'DEBE' | 'HABER'; monto: number }[]
}

function ImportarPolizasModal({ onClose, onDone }: { onClose: () => void; onDone: (n: number) => void }) {
  const [polizas, setPolizas] = useState<PolizaImport[]>([])
  const [errores, setErrores] = useState<string[]>([])
  const [fileName, setFileName] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const pad = (n: number | string) => String(n).padStart(2, '0')
  const parseFecha = (v: any): string | null => {
    if (v instanceof Date && !isNaN(v.getTime())) return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
    const s = String(v ?? '').trim()
    if (!s) return null
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`
    m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/)
    if (m) { let y = m[3]; if (y.length === 2) y = '20' + y; return `${y}-${pad(m[2])}-${pad(m[1])}` }
    return null
  }
  const num = (v: any) => Number(String(v ?? '').replace(/[Qq,\s]/g, '')) || 0
  const codigo = (v: any) => String(v ?? '').trim().split(/[\s\-–]/)[0].trim()

  async function onFile(f: File) {
    setErr(null); setErrores([]); setPolizas([]); setFileName(f.name)
    try {
      const XLSX = await import('xlsx')
      const buf = await f.arrayBuffer()
      const wb = XLSX.read(buf, { cellDates: true })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' })
      // Encontrar fila de encabezado
      let hi = rows.findIndex((r) => r.some((c) => /correlativo/i.test(String(c))) && r.some((c) => /cuenta/i.test(String(c))))
      if (hi < 0) hi = 0
      const head = rows[hi].map((c) => String(c).toLowerCase().trim())
      const col = (re: RegExp) => head.findIndex((h) => re.test(h))
      const iCorr = col(/correlativo/), iCta = col(/cuenta/), iDebe = col(/debe/),
        iHaber = col(/haber/), iFecha = col(/fecha/), iDesc = col(/descrip/)

      const grupos = new Map<string, PolizaImport>()
      let lastCorr = '', lastFecha: string | null = null, lastDesc = ''
      const errs: string[] = []
      for (let r = hi + 1; r < rows.length; r++) {
        const row = rows[r]
        if (!row || row.every((c) => String(c).trim() === '')) continue
        const corr = String(row[iCorr] ?? '').trim() || lastCorr
        const cta = codigo(row[iCta])
        const debe = num(row[iDebe]); const haber = num(row[iHaber])
        const fecha: string | null = (iFecha >= 0 ? parseFecha(row[iFecha]) : null) || lastFecha
        const desc = String(iDesc >= 0 ? (row[iDesc] ?? '') : '').trim() || lastDesc
        if (corr) lastCorr = corr
        if (fecha) lastFecha = fecha
        if (desc) lastDesc = desc
        if (!cta) continue
        if (debe <= 0 && haber <= 0) { errs.push(`Fila ${r + 1}: sin monto en Debe/Haber`); continue }
        if (debe > 0 && haber > 0) { errs.push(`Fila ${r + 1}: tiene Debe Y Haber`); continue }
        if (!corr) { errs.push(`Fila ${r + 1}: sin correlativo`); continue }
        if (!grupos.has(corr)) grupos.set(corr, { correlativo: corr, fecha, descripcion: desc, partidas: [] })
        const g = grupos.get(corr)!
        if (!g.fecha && fecha) g.fecha = fecha
        if (!g.descripcion && desc) g.descripcion = desc
        g.partidas.push({ cuenta_codigo: cta, tipo: debe > 0 ? 'DEBE' : 'HABER', monto: debe > 0 ? debe : haber })
      }

      const lista = Array.from(grupos.values())
      // Validaciones de preview
      for (const g of lista) {
        if (g.partidas.length < 2) errs.push(`Póliza ${g.correlativo}: menos de 2 partidas`)
        if (!g.fecha) errs.push(`Póliza ${g.correlativo}: sin fecha`)
        const d = g.partidas.filter((p) => p.tipo === 'DEBE').reduce((s, p) => s + p.monto, 0)
        const h = g.partidas.filter((p) => p.tipo === 'HABER').reduce((s, p) => s + p.monto, 0)
        if (Math.abs(d - h) > 0.005) errs.push(`Póliza ${g.correlativo}: descuadrada (D ${d.toFixed(2)} ≠ H ${h.toFixed(2)})`)
      }
      setPolizas(lista); setErrores(errs)
      if (lista.length === 0) setErr('No se encontraron pólizas en el archivo.')
    } catch (e: any) {
      setErr('No se pudo leer el archivo: ' + (e?.message ?? e))
    }
  }

  async function importar() {
    if (polizas.length === 0 || errores.length > 0) return
    setSaving(true); setErr(null)
    const { data, error } = await supabase.rpc('importar_polizas_masivo_admin', { p_polizas: polizas })
    setSaving(false)
    if (error) { setErr(error.message); return }
    onDone((data as { creadas?: number })?.creadas ?? polizas.length)
  }

  const totalPartidas = polizas.reduce((s, g) => s + g.partidas.length, 0)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center', background: 'rgba(20,23,28,0.45)' }}>
      <div className="card" style={{ width: 'min(720px, 94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Importar pólizas desde Excel</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--muted-foreground)' }}>
          Columnas: <strong>Correlativo · Cuenta · Debe · Haber · Fecha · Descripción</strong>. Cada correlativo agrupa sus partidas en una póliza. La descripción/fecha se arrastran si quedan vacías.
        </p>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: '1px dashed var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 13, alignSelf: 'flex-start' }}>
          <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f) }} />
          ⬆ Elegir archivo {fileName && `· ${fileName}`}
        </label>

        {err && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--error)' }}>{err}</div>}

        {polizas.length > 0 && (
          <div style={{ marginTop: 14, overflow: 'auto', flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 8 }}>
              {polizas.length} pólizas · {totalPartidas} partidas {errores.length > 0 && <span style={{ color: 'var(--error)' }}>· {errores.length} error(es)</span>}
            </div>
            <table className="table" style={{ fontSize: 12 }}>
              <thead><tr><th>Correlativo</th><th>Fecha</th><th>Descripción</th><th style={{ textAlign: 'center' }}>Partidas</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
              <tbody>
                {polizas.map((g) => {
                  const d = g.partidas.filter((p) => p.tipo === 'DEBE').reduce((s, p) => s + p.monto, 0)
                  const h = g.partidas.filter((p) => p.tipo === 'HABER').reduce((s, p) => s + p.monto, 0)
                  const ok = Math.abs(d - h) < 0.005 && g.partidas.length >= 2 && !!g.fecha
                  return (
                    <tr key={g.correlativo} style={ok ? undefined : { background: 'rgba(220,38,38,0.05)' }}>
                      <td>{g.correlativo}</td>
                      <td>{g.fecha ?? '—'}</td>
                      <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.descripcion || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{g.partidas.length}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {errores.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--error)' }}>
                {errores.slice(0, 8).map((e, i) => <div key={i}>• {e}</div>)}
                {errores.length > 8 && <div>… y {errores.length - 8} más</div>}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 13 }}>Cancelar</button>
          <button onClick={importar} disabled={saving || polizas.length === 0 || errores.length > 0}
            style={{ fontSize: 13, padding: '8px 16px', border: 'none', borderRadius: 8, color: '#fff',
              background: (polizas.length > 0 && errores.length === 0 && !saving) ? 'var(--brand-600, #FF5B04)' : '#D1D5DB',
              cursor: (polizas.length > 0 && errores.length === 0 && !saving) ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Importando…' : `Importar ${polizas.length || ''} pólizas`}
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal de anulación de póliza
function AnularPolizaModal({ asiento, onClose, onDone }: {
  asiento: Asiento; onClose: () => void; onDone: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function anular() {
    if (motivo.trim().length < 5) { setErr('Indicá un motivo (mín 5 caracteres)'); return }
    setSaving(true); setErr(null)
    const { error } = await supabase.rpc('anular_poliza_admin', { p_asiento_id: asiento.id, p_motivo: motivo.trim() })
    setSaving(false)
    if (error) { setErr(error.message); return }
    onDone()
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center', background: 'rgba(20,23,28,0.45)' }}>
      <div className="card" style={{ width: 'min(460px, 92vw)', padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Anular póliza #{asiento.numero}</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--muted-foreground)' }}>
          {asiento.descripcion} · Q {fmtQ(asiento.total_debe).replace('Q ', '')}<br />
          Sale del balance y libro diario, pero queda registrada para auditoría (reversible).
        </p>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' }}>
          MOTIVO DE ANULACIÓN
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
            placeholder="Ej: reclasificación de caja a banco / póliza duplicada"
            style={{ width: '100%', marginTop: 4, fontSize: 13, padding: 8, border: '1px solid var(--border)', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }} />
        </label>
        {err && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--error)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 13 }}>Cancelar</button>
          <button onClick={anular} disabled={saving || motivo.trim().length < 5}
            style={{ fontSize: 13, padding: '8px 16px', border: 'none', borderRadius: 8, color: '#fff',
              background: motivo.trim().length >= 5 && !saving ? 'var(--error)' : '#D1D5DB',
              cursor: motivo.trim().length >= 5 && !saving ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Anulando…' : 'Anular póliza'}
          </button>
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Shared
// -----------------------------------------------------------------------------

function Loading() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 13 }}>
      Cargando…
    </div>
  )
}

function ErrorBox({ error }: { error: string }) {
  return (
    <div
      style={{
        padding: 16,
        background: 'rgba(220, 38, 38, 0.06)',
        border: '1px solid rgba(220, 38, 38, 0.18)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--error)',
        fontSize: 13,
      }}
    >
      Error: {error}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Modal: Nueva cuenta contable (pedido Carlitos 10-ago · RPC 0154)
// -----------------------------------------------------------------------------

function NuevaCuentaModal({
  cuentas,
  onClose,
  onCreated,
}: {
  cuentas: CuentaContable[]
  onClose: () => void
  onCreated: () => void
}) {
  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [padre, setPadre] = useState('')
  const [aceptaPartidas, setAceptaPartidas] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Padres posibles: cualquier cuenta activa (usualmente un rubro)
  const padres = useMemo(
    () => cuentas.filter((c) => c.activa).sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [cuentas],
  )
  const padreSel = padres.find((c) => c.codigo === padre)

  async function crear() {
    setSaving(true)
    setErr(null)
    const { data, error } = await supabase.rpc('crear_cuenta_contable', {
      p_codigo: codigo.trim(),
      p_nombre: nombre.trim(),
      p_padre_codigo: padre,
      p_acepta_partidas: aceptaPartidas,
    })
    setSaving(false)
    if (error) { setErr(error.message); return }
    if ((data as { ok?: boolean })?.ok) onCreated()
  }

  const valido = codigo.trim().length >= 2 && nombre.trim().length >= 3 && !!padre

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center',
        background: 'rgba(20,23,28,0.45)',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: 'min(480px, 92vw)', padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Nueva cuenta contable</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12.5, color: 'var(--muted-foreground)' }}>
          La cuenta hereda clase y naturaleza del padre. Queda registrada en auditoría.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' }}>
            CUENTA PADRE (rubro)
            <select className="input" value={padre} onChange={(e) => setPadre(e.target.value)}
              style={{ width: '100%', marginTop: 4, fontSize: 13 }}>
              <option value="">Seleccionar…</option>
              {padres.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.codigo} · {c.nombre}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' }}>
            CÓDIGO (debe comenzar con {padreSel ? padreSel.codigo : 'el código del padre'})
            <input className="input" value={codigo} onChange={(e) => setCodigo(e.target.value)}
              placeholder={padreSel ? `${padreSel.codigo}XX` : ''} inputMode="numeric"
              style={{ width: '100%', marginTop: 4, fontSize: 13 }} />
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' }}>
            NOMBRE
            <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. COMISIONES PRONET" style={{ width: '100%', marginTop: 4, fontSize: 13 }} />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={aceptaPartidas}
              onChange={(e) => setAceptaPartidas(e.target.checked)} />
            Acepta partidas (cuenta operativa de detalle)
          </label>

          {err && (
            <div style={{ fontSize: 12.5, color: 'var(--error)', background: 'rgba(220,38,38,0.07)', padding: '8px 10px', borderRadius: 8 }}>
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 4 }}>
            <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13 }}>Cancelar</button>
            <button className="btn-primary" onClick={crear} disabled={!valido || saving} style={{ fontSize: 13 }}>
              {saving ? 'Creando…' : 'Crear cuenta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
